import { codecForType } from "./codecs";
import { resolveOperator } from "./operators";
import { parseFilterSearch } from "./parse-filter-search";
import type { FilterFieldsConfig, FilterOperator } from "./types";

export interface BuildListParamsInput<
  TFilters extends Record<string, unknown> = Record<string, unknown>,
> {
  fields: FilterFieldsConfig;
  /** The typed URL search object (`filters` / `search` / `sort`). */
  search?:
    | {
        /**
         * The route's raw `filters`, operator brackets included
         * (`filters[status][notIn]=A`) — this fold parses them out itself, so
         * callers pass `search.filters` straight through.
         */
        filters?: Partial<TFilters>;
        search?: string;
        sort?: string;
      }
    | undefined;
  /** Per-feature saved-view value map (parsed into the filters shape). */
  savedView?: Partial<TFilters> | undefined;
  /** Hard-coded route defaults. */
  defaults?: Partial<TFilters> | undefined;
}

export interface ListParams {
  filters: Record<string, FilterOperator>;
  search?: string;
  sort?: string;
}

function firstDefined(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Server fold — PURE. Folds the active fields through `field.toApiFilter` (or
 * the type's default codec) into the `@birdwatch/client` DSL, layering the
 * three sources with a single documented precedence: `defaults < savedView <
 * url`. Replaces every per-feature `use-unified-*-list-params` + `transform-*`.
 *
 * The operator comes from the URL's own `filters[key][op]` bracket (a saved view
 * or route default supplies values, never operators) and is validated against
 * the field's own list, so an unknown operator degrades to the field's default.
 * `dateRange` / `priceRange` carry no bracket — theirs is derived from which
 * bounds the value fills.
 */
export function buildListParams(input: BuildListParamsInput): ListParams {
  const { fields, search, savedView, defaults } = input;
  const { filters: urlFilters, filtersOp: urlOperators } = parseFilterSearch(
    search?.filters,
  );

  let dsl: Record<string, FilterOperator> = {};
  for (const field of fields) {
    const codec = codecForType[field.type];
    const effective = firstDefined(
      urlFilters[field.key],
      savedView?.[field.key],
      defaults?.[field.key],
    );
    if (codec.isEmptyValue(effective)) continue;
    const operator = resolveOperator(field, urlOperators[field.key], effective);
    const fragment = field.toApiFilter
      ? field.toApiFilter(effective, operator)
      : codec.defaultToApiFilter(effective, field.key, operator);
    dsl = { ...dsl, ...fragment };
  }

  const result: ListParams = { filters: dsl };
  if (search?.search) result.search = search.search;
  if (search?.sort) result.sort = search.sort;
  return result;
}
